import { useState } from 'react';

import { WorkingProfileForm } from '@/components/EditProfilePage/schema';

import LottieTransition from './LottieTransition';
import OnboardingStepOne from './OnboardingStepOne';
import OnboardingStepThree from './OnboardingStepThree';
import OnboardingStepTwo from './OnboardingStepTwo';
import { BrandingResult, OnboardingProfileData } from './onboardingApi';
import { OnboardingStep } from './types';

interface Props {
  onFinalizingChange: (isFinalizing: boolean) => void;
}

function OnboardingFlow({ onFinalizingChange }: Props) {
  const [step, setStep] = useState<OnboardingStep>(1);
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const [scrapedData, setScrapedData] = useState<OnboardingProfileData | null>(
    null,
  );
  const [profileFormData, setProfileFormData] =
    useState<WorkingProfileForm | null>(null);
  const [brandingResult, setBrandingResult] = useState<BrandingResult | null>(
    null,
  );
  const [previewToken, setPreviewToken] = useState<string | null>(null);

  // "Get Started" clicked — show the animation immediately; all API work runs inside it
  const handleStepOneNext = () => setStep('transition');
  // "Create manually" skips the transition entirely
  const handleManual = () => setStep(2);

  const handleTransitionComplete = (
    scraped: OnboardingProfileData | null,
    branding: BrandingResult | null,
    token: string | null,
  ) => {
    setScrapedData(scraped);
    setBrandingResult(branding);
    setPreviewToken(token);
    setProfileFormData(null);
    setStep(2);
  };

  const goToPrev = () =>
    setStep(prev => {
      if (prev === 2) return 1;
      if (prev === 3) return 2;
      return prev;
    });

  const handleStepTwoNext = (formData: WorkingProfileForm) => {
    setProfileFormData(formData);
    setStep(3);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {step === 1 && (
        <OnboardingStepOne
          onNext={handleStepOneNext}
          onManual={handleManual}
          linkedInUrl={linkedInUrl}
          onLinkedInUrlChange={setLinkedInUrl}
        />
      )}
      {step === 'transition' && (
        <LottieTransition
          linkedInUrl={linkedInUrl}
          onComplete={handleTransitionComplete}
        />
      )}
      {step === 2 && (
        <OnboardingStepTwo
          onNext={handleStepTwoNext}
          onBack={goToPrev}
          scrapedData={scrapedData}
          initialFormData={profileFormData}
          brandingResult={brandingResult}
        />
      )}
      {step === 3 && (
        <OnboardingStepThree
          onBack={goToPrev}
          profileFormData={profileFormData}
          previewToken={previewToken}
          onFinalizingChange={onFinalizingChange}
        />
      )}
    </div>
  );
}

export default OnboardingFlow;
